import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useIntl } from 'react-intl';
import { pctSymbol } from '../../../../utils/formatters';
import { useCurrency } from '../../../../hooks/useCurrency';

const PULL_REVENUE_INFORMATION = gql`
  mutation pullRevenueInformationForUser {
    pullRevenueInformationForUser {
      id
      trailer_revenue
      direct_revenue
      transactional_revenue
      fee_based_revenue
      years_practicing
      number_of_clients
      annual_revenue
      recurring_revenue
      assets_under_management
      avg_client_age
      year_1_gross
      year_2_gross
      year_3_gross
      year_4_gross
      year_5_gross
    }
  }
`;

const UPDATE_AUTO_SYNC = gql`
  mutation updateAutoSyncRevenue($updateProductionDataAutomatically: Boolean!) {
    updateCurrentUser(update_production_data_automatically: $updateProductionDataAutomatically)
  }
`;

const RevenueInformationSection = ({ user, onEdit, isReadOnly = false, companyName = 'your firm', refetchUser = () => {}, quickbooksConnected, quickbooksLastSyncedAt, quickbooksConnectUrl, quickbooksDisconnectUrl, quickbooksSyncUrl }) => {
  const featureTypes = user?.company?.feature_types || [];
  const showPullButton = featureTypes.includes('show_pull_latest_revenue_button');
  const showAutoSync = featureTypes.includes('users_choice_update_production_data_automatically');
  const showQuickbooks = featureTypes.includes('quickbooks_connector');
  const intl = useIntl();
  const { symbol: currencySym } = useCurrency();

  const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);
  const [autoSyncConfirmOpen, setAutoSyncConfirmOpen] = useState(false);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(!!user?.update_production_data_automatically);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [qboSyncing, setQboSyncing] = useState(false);
  const [qboSyncMessage, setQboSyncMessage] = useState(null);

  const [pullRevenue, { loading: pullLoading }] = useMutation(PULL_REVENUE_INFORMATION, {
    refetchQueries: ['GetCurrentUserProfile'],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      // Guard: if mutation returned null (server error caught by rescue_from), treat as failure
      if (!data?.pullRevenueInformationForUser) {
        setSyncConfirmOpen(false);
        setSyncError('Sync failed. Please try again or contact support.');
        return;
      }
      setSyncConfirmOpen(false);
      setSyncSuccess(true);
      setTimeout(() => setSyncSuccess(false), 2500);
    },
    onError: (err) => {
      console.error('Revenue sync error:', err);
      setSyncConfirmOpen(false);
      setSyncError(err.message || 'Sync failed. Please try again.');
    },
  });

  const [updateAutoSync] = useMutation(UPDATE_AUTO_SYNC, {
    onCompleted: () => refetchUser(),
    onError: (err) => console.error('Auto-sync update error:', err),
  });

  const handleToggleAutoSync = (newValue) => {
    if (newValue) {
      setAutoSyncEnabled(true);
      setAutoSyncConfirmOpen(true);
    } else {
      setAutoSyncEnabled(false);
      updateAutoSync({ variables: { updateProductionDataAutomatically: false } });
    }
  };

  const handleConfirmAutoSync = () => {
    setAutoSyncConfirmOpen(false);
    updateAutoSync({ variables: { updateProductionDataAutomatically: true } });
  };

  const handleCancelAutoSync = () => {
    setAutoSyncEnabled(false);
    setAutoSyncConfirmOpen(false);
  };

  const handleQboSync = async (e) => {
    e.preventDefault();
    setQboSyncing(true);
    setQboSyncMessage(null);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const response = await fetch(quickbooksSyncUrl, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken, 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      setQboSyncMessage(response.ok ? 'Sync started — data will update shortly.' : (data.error || 'Sync failed.'));
    } catch {
      setQboSyncMessage('Sync failed. Please try again.');
    } finally {
      setQboSyncing(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value || value === 0) return `${currencySym}0`;
    const num = parseFloat(value);
    if (num >= 1000000) {
      return `${currencySym}${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${currencySym}${(num / 1000).toFixed(0)}k`;
    }
    return `${currencySym}${num.toFixed(0)}`;
  };

  // Average year-over-year growth rate — matches the DCF tab (compactAvgGrowth)
  const calculateGrowthRate = () => {
    const vals = [user.year_1_gross, user.year_2_gross, user.year_3_gross, user.year_4_gross, user.year_5_gross]
      .map((v) => Number(v || 0))
      .filter((v) => Number.isFinite(v));
    const cleaned = vals.filter((v) => v); // drop zeros
    if (cleaned.length < 2) return 0;
    const growths = [];
    for (let i = 0; i < cleaned.length - 1; i++) {
      const current = cleaned[i];
      const next = cleaned[i + 1];
      if (current && Number.isFinite(current) && Number.isFinite(next)) {
        growths.push((current - next) / current);
      }
    }
    if (!growths.length) return 0;
    const avg = growths.reduce((s, v) => s + v, 0) / cleaned.length;
    if (!Number.isFinite(avg)) return 0;
    return (avg * 100).toFixed(1);
  };

  return (
    <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-6">
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-xl font-semibold text-succession-text-primary">{intl.formatMessage({ id: 'succession.profile_edit.sections.revenue_information.title' })}</h3>
        {isReadOnly ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-succession-warning/40 bg-succession-warning/10 text-succession-text-secondary">
            <svg className="w-3.5 h-3.5 text-succession-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Managed by {companyName}
          </span>
        ) : (
          <button
            onClick={onEdit}
            className="inline-flex items-center px-3 py-1.5 border border-succession-border rounded-lg text-sm text-succession-text-primary hover:bg-succession-bg-primary transition-colors"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {intl.formatMessage({ id: 'succession.profile_edit.common.edit' })}
          </button>
        )}
      </div>

      {isReadOnly && (
        <p className="text-xs text-succession-text-secondary mb-4">
          Your revenue data is managed by <strong>{companyName}</strong> and updated automatically. Contact your firm to make changes.
        </p>
      )}

      {/* QuickBooks integration — shown when quickbooks_connector feature is enabled */}
      {showQuickbooks && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pt-3 border-t border-succession-border/50">
          <div className="flex items-center gap-2.5">
            <img
              src="https://findbob-production.s3.us-west-2.amazonaws.com/images/qbo_logomark.png"
              alt="QuickBooks"
              className="w-7 h-7 flex-shrink-0 rounded-md object-contain"
            />
            <div>
              <p className="text-sm font-medium text-succession-text-primary leading-tight">QuickBooks Online</p>
              {quickbooksConnected && quickbooksLastSyncedAt && (
                <p className="text-xs text-succession-text-muted">
                  Last synced: {new Date(quickbooksLastSyncedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              )}
              {qboSyncMessage && (
                <p className="text-xs text-succession-text-secondary mt-0.5">{qboSyncMessage}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {quickbooksConnected ? (
              <>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  Connected
                </span>
                <button
                  onClick={handleQboSync}
                  disabled={qboSyncing}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-succession-border text-succession-text-primary hover:bg-succession-bg-primary disabled:opacity-50 transition-colors"
                >
                  {qboSyncing ? 'Syncing…' : 'Sync'}
                </button>
              </>
            ) : (
              <a
                href={quickbooksConnectUrl}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-succession-accent text-white hover:opacity-90 transition-opacity"
              >
                Connect QuickBooks
              </a>
            )}
          </div>
        </div>
      )}

      {/* Sync controls — shown whenever feature flags are enabled, regardless of read-only */}
      {(showPullButton || showAutoSync) && (
        <div className="flex flex-wrap items-center gap-3 mb-5 pt-3 border-t border-succession-border/50">
              {showPullButton && (
                <button
                  onClick={() => { setSyncError(null); !syncSuccess && setSyncConfirmOpen(true); }}
                  disabled={pullLoading}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-all duration-300 ${
                    syncSuccess
                      ? 'border-succession-success/50 bg-succession-success/10 text-succession-success'
                      : 'border-succession-border text-succession-text-primary hover:bg-succession-bg-primary disabled:opacity-60 disabled:cursor-not-allowed'
                  }`}
                >
                  {pullLoading ? (
                    <>
                      <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Syncing…
                    </>
                  ) : syncSuccess ? (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      Synced!
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Sync Latest Data
                    </>
                  )}
                </button>
              )}

              {showAutoSync && (
                <label className="inline-flex items-center gap-2.5 cursor-pointer select-none">
                  {/* Toggle pill */}
                  <button
                    role="switch"
                    aria-checked={autoSyncEnabled}
                    onClick={() => handleToggleAutoSync(!autoSyncEnabled)}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-succession-success focus-visible:ring-offset-1 ${
                      autoSyncEnabled ? 'bg-succession-success' : 'bg-succession-border'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm ring-0 transition-transform duration-200 ${
                        autoSyncEnabled ? 'translate-x-4' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <span className="text-sm text-succession-text-secondary">Auto-sync daily</span>
                </label>
              )}
        </div>
      )}

      {syncError && (
        <div className="mb-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-succession-error/10 border border-succession-error/30 text-sm text-succession-error">
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {syncError}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-succession-bg-primary rounded-lg p-4 border border-succession-border">
          <div className="flex items-center text-succession-text-muted text-sm mb-1">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {intl.formatMessage({ id: 'succession.profile_edit.sections.revenue_information.gross_production' })}
          </div>
          <div className="text-2xl font-bold text-succession-text-primary">
            {formatCurrency(user.gross_production)}
          </div>
        </div>
        <div className="bg-succession-bg-primary rounded-lg p-4 border border-succession-border">
          <div className="flex items-center text-succession-text-muted text-sm mb-1">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {intl.formatMessage({ id: 'succession.profile_edit.sections.revenue_information.recurring_revenue' })}
          </div>
          <div className="text-2xl font-bold text-succession-text-primary">
            {formatCurrency(user.recurring_revenue)}
          </div>
        </div>
        <div className="bg-succession-bg-primary rounded-lg p-4 border border-succession-border">
          <div className="flex items-center text-succession-text-muted text-sm mb-1">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            {intl.formatMessage({ id: 'succession.profile_edit.sections.revenue_information.five_year_growth_rate' })}
          </div>
          <div className="text-2xl font-bold text-succession-text-primary">
            {calculateGrowthRate()}{pctSymbol()}
          </div>
        </div>
      </div>

      {/* Sync confirmation dialog */}
      {syncConfirmOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
          onClick={() => setSyncConfirmOpen(false)}
        >
          <div
            className="bg-succession-bg-secondary border border-succession-border rounded-lg shadow-xl p-6 max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2.5 mb-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-succession-success/10 flex items-center justify-center">
                <svg className="w-4 h-4 text-succession-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-succession-text-primary">Sync Production Data</h3>
            </div>
            <p className="text-sm text-succession-text-secondary mb-5 leading-relaxed">
              This will pull the most recent data from your firm and overwrite any manually entered revenue information. Do you wish to proceed?
            </p>
            <div className="flex gap-2.5 justify-end">
              <button
                onClick={() => setSyncConfirmOpen(false)}
                className="px-3.5 py-1.5 text-sm border border-succession-border rounded-lg text-succession-text-primary hover:bg-succession-bg-primary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => pullRevenue()}
                disabled={pullLoading}
                className="px-3.5 py-1.5 text-sm bg-succession-success text-white rounded-lg hover:opacity-80 transition-opacity disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {pullLoading ? (
                  <>
                    <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Syncing…
                  </>
                ) : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auto-sync confirmation dialog */}
      {autoSyncConfirmOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
          onClick={handleCancelAutoSync}
        >
          <div
            className="bg-succession-bg-secondary border border-succession-border rounded-lg shadow-xl p-6 max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2.5 mb-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-succession-warning/10 flex items-center justify-center">
                <svg className="w-4 h-4 text-succession-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-succession-text-primary">Enable Daily Auto-Sync</h3>
            </div>
            <p className="text-sm text-succession-text-secondary mb-5 leading-relaxed">
              Your production data will be overwritten by the daily update and you will lose any manual changes you've made.
            </p>
            <div className="flex gap-2.5 justify-end">
              <button
                onClick={handleCancelAutoSync}
                className="px-3.5 py-1.5 text-sm border border-succession-border rounded-lg text-succession-text-primary hover:bg-succession-bg-primary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAutoSync}
                className="px-3.5 py-1.5 text-sm bg-succession-success text-white rounded-lg hover:opacity-80 transition-opacity inline-flex items-center gap-1.5"
              >
                Enable
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

RevenueInformationSection.propTypes = {
  user: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  companyName: PropTypes.string,
  refetchUser: PropTypes.func,
};

export default RevenueInformationSection;
